import { Divider, Paper, Stack } from '@mantine/core';
import { MRT_RowSelectionState } from 'mantine-react-table';
import { useEffect, useMemo, useState } from 'react';
import {
    DialogSize,
    ResizableDialog,
} from '../../../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { MantineColumn, MantineTable } from '../../../../../../react-commons/components/table/MantineTable';
import { EditableDisplayBar } from '../../../../../../react-commons/views/EditableDisplayBar';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    ActivitiesPropertyValidation,
    useActivitiesActions,
} from '../../../../resource/resources/artifacts/activities/activitiesResource';
import { useProjectVersionActions } from '../../../../resource/resources/artifacts/project/projectVersionResource';
import { ControlledPropertyInputs } from '../../base/property-bases/read-only/ControlledPropertyInputs';
import { Controlled_ProjectVersionTextField } from '../../base/reference-text-fields/Controlled_ProjectVersionTextField';

type ActivityRowResource = {
    originalName: string;
    objectId: string;
    id: string;
    nameOverride: string;
};

export const CloneActivitiesDialog = ({
    onClose,
    projectId,
    projectVersionId,
}: {
    onClose: () => void;
    projectId: string;
    projectVersionId: string;
}) => {
    const [selectedSourceProjectVersion, setSelectedSourceProjectVersion] = useState<string | null>(null);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [activityRows, setActivityRows] = useState<Array<ActivityRowResource>>([]);

    const activitiesActions = useActivitiesActions();
    const projectVersionActions = useProjectVersionActions();

    const projectVersionQuery = projectVersionActions.useGetResource(selectedSourceProjectVersion);
    const activitiesQuery = activitiesActions.useGetAllResources(selectedSourceProjectVersion);
    const cloneActivities = activitiesActions.useCopyResources();

    const columns = useMemo((): Array<MantineColumn<ActivityRowResource>> => {
        return [
            {
                Header: null,
                type: 'string',
                accessorKey: 'originalName',
                header: 'Activity',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.String
                        readOnly={true}
                        nullable={false}
                        value={original.originalName}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.ACTIVITIES,
                            resourceId: original.id,
                            propertyIdentifier: ActivitiesPropertyValidation.NAME,
                        }}
                    />
                ),
            },
            {
                Header: null,
                type: 'string',
                accessorKey: 'nameOverride',
                header: 'New Name',
                Cell: ({ row: { original, index } }) => (
                    <ControlledPropertyInputs.String
                        readOnly={false}
                        nullable={false}
                        value={original.nameOverride}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.ACTIVITIES,
                            resourceId: original.id,
                            propertyIdentifier: ActivitiesPropertyValidation.NAME,
                        }}
                        onChange={(value) => {
                            setActivityRows((prev) =>
                                prev.map((row) => (row.id === original.id ? { ...row, nameOverride: value } : row))
                            );
                        }}
                    />
                ),
            },
        ];
    }, []);

    useEffect(() => {
        if (projectVersionQuery.data && activitiesQuery.data) {
            const versionNumber = projectVersionQuery.data.versionInfo.versionNumber;
            setRowSelection({});
            setActivityRows(
                activitiesQuery.data.map((activity) => ({
                    originalName: activity.name,
                    id: activity.id,
                    objectId: activity.objectId,
                    nameOverride: `[Cloned from version ${versionNumber}] ${activity.name}`,
                }))
            );
        }
    }, [activitiesQuery.data, projectVersionQuery.data]);

    return (
        <ResizableDialog
            title={'Copy Activities from Project Version'}
            onClose={onClose}
            canClose={true}
            disableBackdropClick={true}
            dialogSize={DialogSize.LARGE}
        >
            <Paper className={'main-window-content'} p={0}>
                <Stack>
                    <Controlled_ProjectVersionTextField
                        id={selectedSourceProjectVersion}
                        projectId={projectId}
                        readOnly={false}
                        propertyValidation={null}
                        label={'Source Project Version'}
                        onChange={(value) => setSelectedSourceProjectVersion(value)}
                        projectVersionIdToFilterOut={projectVersionId}
                    />
                    <Divider />
                    <div
                        style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                            overflowY: 'auto',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                minHeight: 575,
                                position: 'relative',
                            }}
                        >
                            <MantineTable
                                columns={columns}
                                data={activityRows}
                                label={'Activities'}
                                enableActions={false}
                                rowSelectionState={{ rowSelection, setRowSelection }}
                                initialPageSize={10}
                                fillParent={true}
                                tableOptions={{
                                    enableSorting: true,
                                    enableBottomToolbar: true,
                                    enablePagination: true,
                                    enableTopToolbar: true,
                                    enableStickyHeader: true,
                                    autoResetPageIndex: false,
                                }}
                            />
                        </div>
                        <EditableDisplayBar
                            isDirty={Object.keys(rowSelection).length > 0 && selectedSourceProjectVersion !== null}
                            handleCancelButtonClick={onClose}
                            saveButtonLabel={'Copy'}
                            handleSaveButtonClick={() => {
                                const selectedActivities = activityRows.mapNotNull((it, index) => {
                                    if (!rowSelection[index]) {
                                        return null;
                                    }
                                    return it;
                                });

                                cloneActivities.mutate(
                                    {
                                        sourceProjectVersionId: selectedSourceProjectVersion ?? '',
                                        targetProjectVersionId: projectVersionId,
                                        activitiesList: selectedActivities.map((value) => ({
                                            activityObjectId: value.objectId,
                                            insertName: value.nameOverride,
                                        })),
                                    },
                                    {
                                        onSettled: (data) => {
                                            if (data?.successful) {
                                                onClose();
                                            }
                                        },
                                    }
                                );
                            }}
                        />
                    </div>
                </Stack>
            </Paper>
        </ResizableDialog>
    );
};
